import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import AlertsLink from '~/components/app/AlertsLink';
import CommunityLink from '~/components/app/CommunityLink';
import CurrentPagesOverviewLink from '~/components/app/CurrentPagesOverviewLink';
import DashboardLink from '~/components/app/DashboardLink';
import IssuesOverviewLink from '~/components/app/IssuesOverviewLink';
import PlatformLink from '~/components/app/PlatformLink';
import WebsiteSettingsLink from '~/components/app/WebsiteSettingsLink';
import MainNavigationLayout from '~/components/patterns/navigations/MainNavigationLayout';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';

import {
	type messages
} from './MainNavigation';



type Props = {
	activeItemIndex: number | undefined,
	messages: typeof messages,
	websiteId: CK.WebsiteId | null,
};

/**
 * Old Navigation Component
 *
 * Renders the old (purple) navigation layout when REBRANDING.navbar is false
 * or when user toggles to old navigation.
 *
 * Structure:
 * - Primary items: Dashboard, Pages, Platform, Issues, Alerts
 * - Secondary items: Settings, Community
 */
const OldNavigation: React.FC<Props> = (props) => {
	const {
		activeItemIndex,
		messages,
		websiteId,
	} = props;

	return (
		<MainNavigationLayout
			activeItemIndex={activeItemIndex}
			primaryItems={[
				<DashboardLink
					key="dashboard-link"
					websiteId={websiteId}
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon type={NavigationIconType.Dashboard} />
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								label={(
									<FormattedMessage {...messages.dashboard} />
								)}
							/>
						);
					}}
				</DashboardLink>,
				<CurrentPagesOverviewLink key="pages-overview-link">
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon type={NavigationIconType.Pages} />
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								label={(
									<FormattedMessage {...messages.pages} />
								)}
							/>
						);
					}}
				</CurrentPagesOverviewLink>,
				<PlatformLink
					key="platform-link"
					websiteId={websiteId}
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon type={NavigationIconType.Platform} />
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								label={(
									<FormattedMessage {...messages.platform} />
								)}
							/>
						);
					}}
				</PlatformLink>,
				<IssuesOverviewLink
					key="issues-overview-link"
					websiteId={websiteId}
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon type={NavigationIconType.Issues} />
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								label={(
									<FormattedMessage {...messages.issues} />
								)}
							/>
						);
					}}
				</IssuesOverviewLink>,
				<AlertsLink
					keepSettings={false}
					key="alerts-link"
					websiteId={websiteId}
				>
					{({ isActive, isDimmed }) => {
						return (
							<MenuItemLayout
								icon={(
									<NavigationIcon type={NavigationIconType.Alerts} />
								)}
								isActive={isActive}
								isDimmed={isDimmed}
								label={(
									<FormattedMessage {...messages.alerts} />
								)}
							/>
						);
					}}
				</AlertsLink>,
			]}
			secondaryItems={[
				// Always show all items in Phase 2 (56px vertical sidebar)
				<WebsiteSettingsLink
					key="website-settings-link"
					websiteId={websiteId}
				>
					{({ isActive, isDimmed }) => (
						<MenuItemLayout
							icon={(
								<NavigationIcon type={NavigationIconType.Settings} />
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							label={(
								<FormattedMessage {...messages.settings} />
							)}
						/>
					)}
				</WebsiteSettingsLink>,
				<CommunityLink
					key="community-link"
				>
					{({ isActive, isDimmed }) => (
						<MenuItemLayout
							icon={(
								<NavigationIcon type={NavigationIconType.Community} />
							)}
							isActive={isActive}
							isDimmed={isDimmed}
							label={(
								<FormattedMessage {...messages.community} />
							)}
						/>
					)}
				</CommunityLink>,
			]}
		/>
	);
};

export default OldNavigation;
